/**
 * SignallingService — Bridges WebRTC ↔ Firebase RTDB for mesh voice signalling.
 *
 * Adapted from Dive Deep Dating's Firestore-based signalling. DrawLab uses RTDB
 * because the rest of the app already uses it for drawing collaboration.
 *
 * Data model:
 *   rooms/{code}/voice/
 *     participants/{userId}: { nickname, joinedAt }
 *     signalling/{channelKey}/
 *       offer: { from, type, sdp, timestamp }
 *       answer: { from, type, sdp, timestamp }
 *       candidates/{auto}: { from, candidate, sdpMid, sdpMLineIndex, timestamp }
 *
 * Channel key is deterministic: smaller userId first, sorted alphabetically:
 *   channelKey(userA, userB) = userA < userB ? `${userA}_${userB}` : `${userB}_${userA}`
 *
 * This way both sides of the channel know which key to read/write to
 * without coordinating, and we only need one channel per pair of users.
 */

import {
  ref,
  set,
  push,
  onValue,
  onChildAdded,
  off,
  remove,
  serverTimestamp,
  onDisconnect,
} from 'firebase/database';
import { database } from '@/firebase';

const MAX_SDP_SIZE = 10000;      // SDP offer/answer max size (typical 1-5KB)
const MAX_CANDIDATES_PER_PEER = 50; // Prevent candidate flood

/**
 * Compute the deterministic channel key for a pair of users.
 * Both users in the pair will always compute the same key.
 */
export function computeChannelKey(userIdA, userIdB) {
  return userIdA < userIdB ? `${userIdA}_${userIdB}` : `${userIdB}_${userIdA}`;
}

export class SignallingService {
  constructor(roomCode, localUserId) {
    this.roomCode = roomCode;
    this.localUserId = localUserId;
    this.activeListeners = [];
    this.candidateCounts = new Map(); // per-channel candidate count (rate limit)
  }

  // ── RTDB Path Helpers ──────────────────────────────────────────────────────

  _participantsRef() {
    return ref(database, `rooms/${this.roomCode}/voice/participants`);
  }

  _participantRef(userId) {
    return ref(database, `rooms/${this.roomCode}/voice/participants/${userId}`);
  }

  _channelRef(channelKey) {
    return ref(database, `rooms/${this.roomCode}/voice/signalling/${channelKey}`);
  }

  _offerRef(channelKey) {
    return ref(database, `rooms/${this.roomCode}/voice/signalling/${channelKey}/offer`);
  }

  _answerRef(channelKey) {
    return ref(database, `rooms/${this.roomCode}/voice/signalling/${channelKey}/answer`);
  }

  _candidatesRef(channelKey) {
    return ref(database, `rooms/${this.roomCode}/voice/signalling/${channelKey}/candidates`);
  }

  // ── Participants ───────────────────────────────────────────────────────────

  /**
   * Mark this user as present in voice chat. Cleaned up on disconnect
   * via RTDB's onDisconnect — handles browser tab close, network loss, etc.
   */
  async joinVoice(nickname) {
    const participantRef = this._participantRef(this.localUserId);
    // Register onDisconnect cleanup BEFORE writing the participant entry
    onDisconnect(participantRef).remove();
    await set(participantRef, {
      nickname: (nickname || '').slice(0, 30),
      joinedAt: serverTimestamp(),
    });
  }

  /**
   * Remove this user from voice chat.
   */
  async leaveVoice() {
    try {
      await remove(this._participantRef(this.localUserId));
    } catch {
      // Ignore — may have already been removed via onDisconnect
    }
  }

  /**
   * Subscribe to the voice participants list.
   * @param {(participants: Record<string, {nickname, joinedAt}>) => void} callback
   * @returns unsubscribe function
   */
  subscribeToParticipants(callback) {
    const ref = this._participantsRef();
    const unsub = onValue(ref, (snap) => {
      callback(snap.val() || {});
    });
    const cleanup = () => off(ref, 'value', unsub);
    this.activeListeners.push(cleanup);
    return cleanup;
  }

  // ── SDP Exchange (pairwise) ────────────────────────────────────────────────

  /**
   * Write an SDP offer to the shared channel between local user and remote peer.
   */
  async writeOffer(remotePeerId, offer) {
    const sdp = String(offer.sdp || '');
    if (sdp.length > MAX_SDP_SIZE) throw new Error('SDP offer too large');

    const channelKey = computeChannelKey(this.localUserId, remotePeerId);
    await set(this._offerRef(channelKey), {
      from: this.localUserId,
      type: offer.type,
      sdp,
      timestamp: Date.now(),
    });
  }

  /**
   * Write an SDP answer to the shared channel.
   */
  async writeAnswer(remotePeerId, answer) {
    const sdp = String(answer.sdp || '');
    if (sdp.length > MAX_SDP_SIZE) throw new Error('SDP answer too large');

    const channelKey = computeChannelKey(this.localUserId, remotePeerId);
    await set(this._answerRef(channelKey), {
      from: this.localUserId,
      type: answer.type,
      sdp,
      timestamp: Date.now(),
    });
  }

  /**
   * Subscribe to the offer slot on a channel — triggers when a remote peer writes
   * an offer targeting us. Callback receives the offer object.
   */
  subscribeToOffer(remotePeerId, callback) {
    const channelKey = computeChannelKey(this.localUserId, remotePeerId);
    const offerRef = this._offerRef(channelKey);
    const unsub = onValue(offerRef, (snap) => {
      const data = snap.val();
      if (!data || data.from === this.localUserId) return; // Not for us
      callback({ type: data.type, sdp: data.sdp });
    });
    const cleanup = () => off(offerRef, 'value', unsub);
    this.activeListeners.push(cleanup);
    return cleanup;
  }

  /**
   * Subscribe to the answer slot on a channel — triggers when the remote peer
   * writes an answer to our offer.
   */
  subscribeToAnswer(remotePeerId, callback) {
    const channelKey = computeChannelKey(this.localUserId, remotePeerId);
    const answerRef = this._answerRef(channelKey);
    const unsub = onValue(answerRef, (snap) => {
      const data = snap.val();
      if (!data || data.from === this.localUserId) return; // Our own echo
      callback({ type: data.type, sdp: data.sdp });
    });
    const cleanup = () => off(answerRef, 'value', unsub);
    this.activeListeners.push(cleanup);
    return cleanup;
  }

  // ── ICE Candidates (pairwise) ──────────────────────────────────────────────

  /**
   * Write a local ICE candidate to the shared channel.
   */
  async writeIceCandidate(remotePeerId, candidate) {
    const channelKey = computeChannelKey(this.localUserId, remotePeerId);

    // Per-channel rate limit
    const count = (this.candidateCounts.get(channelKey) || 0) + 1;
    this.candidateCounts.set(channelKey, count);
    if (count > MAX_CANDIDATES_PER_PEER) return;

    await push(this._candidatesRef(channelKey), {
      from: this.localUserId,
      candidate: String(candidate.candidate || ''),
      sdpMid: candidate.sdpMid ?? null,
      sdpMLineIndex: candidate.sdpMLineIndex ?? null,
      timestamp: Date.now(),
    });
  }

  /**
   * Subscribe to ICE candidates from the remote peer on the shared channel.
   * Filters out our own candidates by checking the `from` field.
   */
  subscribeToRemoteCandidates(remotePeerId, callback) {
    const channelKey = computeChannelKey(this.localUserId, remotePeerId);
    const candidatesRef = this._candidatesRef(channelKey);
    const unsub = onChildAdded(candidatesRef, (snap) => {
      const data = snap.val();
      if (!data || data.from === this.localUserId) return; // Skip our own
      const { from, timestamp, ...candidate } = data;
      callback(candidate);
    });
    const cleanup = () => off(candidatesRef, 'child_added', unsub);
    this.activeListeners.push(cleanup);
    return cleanup;
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  /**
   * Remove the signalling channel between this user and a specific remote peer.
   * Called when a peer leaves voice.
   */
  async clearChannel(remotePeerId) {
    const channelKey = computeChannelKey(this.localUserId, remotePeerId);
    try {
      await remove(this._channelRef(channelKey));
    } catch {
      // Non-fatal
    }
    this.candidateCounts.delete(channelKey);
  }

  /**
   * Remove all active listeners.
   */
  removeAllListeners() {
    this.activeListeners.forEach((unsub) => {
      try { unsub(); } catch { /* already unsubscribed */ }
    });
    this.activeListeners = [];
    this.candidateCounts.clear();
  }
}

export default SignallingService;
